<template>
  <div v-if="pathway && outcome" class="view-pathway">
    <Modal
      v-model="showModal"
      class="progress-animation-modal"
      :footer="false"
      :header="false"
      :backdrop="false"
      data-test="pp-modal-animation"
    >
      <template #default>
        <div style="margin-inline: auto" class="text-center">
          <h3>Good luck on your journey!</h3>
        </div>
        <div class="animation-container">
          <LottieProgressBar ref="lottieProgressBar" width="75%" />
        </div>
      </template>
    </Modal>
    <div class="view-pathway-description">
      <div class="pathway-header">
        <div class="pathway-header-text">
          <h2 data-test="pp-view-pathway-title">
            <b>Pathway: </b>{{ cms.asText(pathway.data.title) }}
          </h2>
          <div v-html="cms.asHtml(pathway.data.short_description)" data-test="pp-short-desc"></div>
        </div>
        <div class="pathway-header-image">
          <img
            :src="pathway.data.title_image.url"
            :alt="cms.asText(pathway.data.title)"
            data-test="pp-view-pathway-img"
          />
        </div>
      </div>
      <div class="pathway-section">
        <p v-html="cms.asHtml(pathway.data.description)" data-test="pp-view-pathway-desc"></p>
      </div>
      <div>
        <ul class="practicalities-list" data-test="pp-pathway-practicalities">
          <li
            v-for="(p, index) in pathway.data.practicalities"
            :key="`practicalities` + index"
            class="practicalities-list-item"
          >
            <div class="practicality-title">
              <img src="../../assets/light-bulb-icon.svg" :alt="`benefit` + (index + 1)" />
              <p data-test="pp-practicalities-title">{{ cms.asText(p.practicality_title) }}</p>
            </div>
            <p
              class="practicality-desc-item"
              data-test="pp-practicality-desc-item"
              v-html="cms.asHtml(p.practicality_description)"
            ></p>
          </li>
        </ul>
      </div>
    </div>
    <div class="btn-container">
      <button
        @click="selectPathway"
        class="btn btn-primary"
        data-test="pp-pathway-save"
        type="button"
      >
        Go!
      </button>
    </div>
    <div v-if="errorMessage" class="error-message">
      <p data-test="pp-error-message" class="text-danger">{{ errorMessage }}</p>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Modal } from "uiv";
import { useRoute } from "vue-router";
import { CmsGoalData, CmsPersonalPathway } from "../../services/cms/cms-type-definitions";
import { Analytics } from "../../services/analytics/analytics";
import cms from "../../services/cms/cms";
import LoadingPage from "../../views/loading-page.vue";
import { PersonalPathwayUserProgressApiModel } from "../../api-models";
import { csContext } from "../../services/cs-context";
import { useAxios } from "../../composables/api";
import LottieProgressBar from "../lottie-progress-bar.vue";

const props = defineProps<{ outcome: CmsGoalData }>();

const emit = defineEmits(["update-stepper", "hide-stepper"]);

const route = useRoute();

const pathwayUid = ref<string>(route.params.pathwayUid as string);
const pathway = ref<CmsPersonalPathway | null>(null);
const outcomeUid = ref<string>(route.params.outcomeUid as string);
const errorMessage = ref<string | null>(null);
const showModal = ref(false);
const routeName = ref(useRoute().name);
const lottieProgressBar = ref();

const selectPathway = (): void => {
  errorMessage.value = null;
  const errorText = "There was a problem selecting this pathway";

  if (!pathway.value || !pathway.value.data.goal_relationship) {
    errorMessage.value = errorText;
    throw new Error("Need to have pathway & goal in place to save");
  }

  if (!pathway.value.uid || !pathway.value.data.goal_relationship.uid) {
    errorMessage.value = errorText;
    throw new Error("Pathway and outcome need to have UIDs set");
  }

  const selectedPathway: PersonalPathwayUserProgressApiModel = {
    userId: csContext.user.id,
    cmsPersonalOutcomeId: pathway.value.data.goal_relationship.id,
    cmsPersonalOutcomeUid: outcomeUid.value,
    cmsPersonalOutcomeTitle: cms.asText(pathway.value.data.goal_relationship.data?.full_title),
    cmsPersonalPathwayId: pathway.value.id,
    cmsPersonalPathwayUid: pathwayUid.value,
    cmsPersonalPathwayTitle: cms.asText(pathway.value.data.title),
    isCurrent: true,
    totalPathwayStepCount: pathway.value.data.pathway_steps.length,
    isCompleted: false,
  };

  const { errorStatus, promise } = useAxios("/api/personalpathway", {
    httpMethod: "POST",
    data: selectedPathway,
  });

  promise.then(() => {
    if (errorStatus.value) {
      errorMessage.value = errorText;
    } else {
      emit("hide-stepper");
      showModal.value = true;
      lottieProgressBar.value.playAnimation();
      setTimeout(() => {
        window.location.href = "/home?view=MyDashboard";
      }, 3000);
    }
  });
};

onMounted(async () => {
  emit("update-stepper", routeName);
  pathway.value = await cms.getByUid("cms-personal-pathway", pathwayUid.value);
  Analytics.action("Viewed Personal Pathway Details", "PersonalPathwayActions", {
    outcomeTitle: cms.asText(props.outcome.full_title),
    outcomeUid: outcomeUid.value,
    PathwayTitle: cms.asText(pathway?.value?.data?.title),
    PathwayUid: pathwayUid.value,
  });
});
</script>

<style scoped lang="less">
@import "../../styles/outcomes/pathway-summary";
</style>

<style lang="less">
@import "../../styles/outcomes/prismic-pathway-summary";
</style>
